// @flow
// $FlowFixMe: useState available in flow-bin 0.82
import React, { Component, useState } from 'react';
import styled from 'styled-components';
import createDebugger from 'debug';
const debug = createDebugger('app:components:BaseTag'); // eslint-disable-line no-unused-vars
import { ThemeType } from 'trustar-2.0/types';
import { IconLookup } from 'trustar-2.0/components';
import { IconButtonPure } from 'trustar-2.0/components/Button';
import {
  MitreAttackThreatModelContext,
  getters as MitreGetters
} from 'trustar-2.0/contexts/mitre-attack';
import {List} from 'immutable';
import { TooltipWrapper } from '../TooltipWrapper';
import { v4 as uuid } from 'uuid';
import {MitreInfoTagTooltip} from './MitreTagTooltipAndMetadata';

// Style for Main Components (things you may export)
const StyledRemovableTag = styled.div`
  display: inline-flex;
  align-items: center;
  min-width: 45px;
  height: ${({ small }) => (small ? '1.5rem' : '2rem')};
  font-size: ${({ small }) => (small ? '0.75rem' : '1rem')};
  border-radius: 3px;
  margin-right: 5px;
  margin-bottom: 5px;
  background: ${({ theme }) => theme.ThemeColors.LIGHT.hex};
  color: ${({ theme }) => theme.ThemeColors.DARK.hex};

  flex-shrink: 0;

  ${({ isMitre }) =>
  isMitre &&
    `
    background: #FACCBB;
    && > button:hover {
      background: #FACCBB;
    }
  `}
`;

const StyledTagLabel = styled.span`
  white-space: nowrap;
  display: block;
  pointer-events: none;
  padding: 0 0.5rem;
`;

// For some reason, IconButtonPure is not allowing itself to be styled in this context.
// https://github.com/styled-components/styled-components/issues/1449#issuecomment-420087359
const StyledCloseButton = styled(props => <IconButtonPure {...props} />)`
  && {
    display: flex;
    text-align: center;
    border-radius: 0;
    padding: 0 !important;
    border: 0;
    height: 100%;
    border-right: 1px solid ${({ theme }) => theme.ThemeColors.DARK.hex};
    color: ${({ theme }) => theme.ThemeColors.DARK.hex};
    :hover {
      background: initial;
      opacity: 0.8;
      border-right: 1px solid ${({ theme }) => theme.ThemeColors.DARK.hex};
      border-top-left-radius: 3px;
      border-bottom-left-radius: 3px;
    }

    i {
      padding: 0.45rem;
      font-size: ${({ small }) => (small ? '.75rem' : '1rem')};
    }
  }
`;

const initialValues = {
  small: false,
  tagLabel: '',
  maxTagLength: 32
};

// Bindings
type RemovableTagProps = {
  theme?: ThemeType,
  onRemove?: Function,
  onClick?: Function,
  tagLabel: string,
  maxTagLength: number,
  isRemovable?: boolean,
  preventTooltip?: boolean,
  small: boolean,
  enclave?: any,
  key?: any,
};

const BaseTag = ({
  tagLabel = '',
  small = false,
  isRemovable = false,
  maxTagLength = 32,
  preventTooltip = false,
  onRemove,
  onClick,
  enclave,
  key, // For rendering in iterated list, this prop has to be used
  ...otherProps
}: RemovableTagProps) => {
  // $FlowFixMe: useContext isn't in this flow def.
  const mitreAttackThreatModel = React.useContext(
    MitreAttackThreatModelContext
  );

  const [tagId] = useState(uuid());

  const maxLengthString = tagLabel =>
    tagLabel.length > maxTagLength
      ? `${tagLabel.substring(0, maxTagLength)}...`
      : tagLabel;

  const mitreTagMap = MitreGetters.getTagMap(mitreAttackThreatModel);
  const mitreInfoIfExists = mitreTagMap.get(tagLabel);
  const relevantTooltip = mitreInfoIfExists ? <MitreInfoTagTooltip mitreTagInfo={mitreInfoIfExists}>{tagLabel}</MitreInfoTagTooltip> :
    <TagTooltip mitreInfoIfExists={mitreInfoIfExists} enclave={enclave}>{tagLabel}</TagTooltip>;

  return (
    <TooltipWrapper
      key={key || tagId}
      placement='top'
      id={tagId}
      autohide={false}
      preventOpen={preventTooltip}
      tooltip={relevantTooltip}>
      <StyledRemovableTag small={small} isMitre={mitreInfoIfExists} {...otherProps}>
        {isRemovable && (
          <StyledCloseButton
            small={small}
            fill='none'
            size='small'
            onClick={onRemove}
            icon={IconLookup.CROSS}
          />
        )}
        <StyledTagLabel
          title={tagLabel}
          maxTagLength={maxTagLength}
          onClick={onClick}>
          {maxLengthString(tagLabel)}
        </StyledTagLabel>
      </StyledRemovableTag>
    </TooltipWrapper>
  );
};

const StyledTooltip = styled.span`
  display: inline-block;
  background-color: ${({ theme }) => theme.ThemeColors.SURFACE.hex};
  color: ${({ theme }) => theme.ThemeColors.TEXT.hex};
  border: 1px solid ${({ theme }) => theme.ThemeColors.BORDER.hex};
  padding: ${({noPadding}) => noPadding? 0 : '0.5rem'};
`;

const TagTooltip = ({ enclave, children, ...otherProps }) => {
  return (
    <StyledTooltip {...otherProps}>
      {enclave && enclave.get('name', '') + ': '}
      {children}
    </StyledTooltip>
  );
};

export { BaseTag as Tag };



// WEBPACK FOOTER //
// ./web_modules/trustar-2.0/components/Tags/BaseTag.js